import { index, jsonb, pgTable, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// ACHIEVEMENT ALIGNMENT REVIEW — CLEANUP-CREW lane (docs/design/cleanup-crew-
// neon.sql is the SQL twin of this file).
//
// The failure class this closes: a TRUE fact with a FALSE address — a real
// achievement (an "integration layer" story, say) rendering under the WRONG
// employer. Every downstream artifact (résumé, cover letter, interview prep)
// composed from a misfiled card lies about the person's history even though
// the underlying fact is real. AlignmentReviewService (apps/api/services/
// corpus/AlignmentReviewService.ts) is the deterministic v1 DETECTOR — pure
// SQL + text heuristics, no model call — that flags a SUSPECT card + evidence.
//
// THE GOVERNING LAW (Jason, binding): corrections are USER-DRIVEN. The system
// SURFACES a suspect with evidence; the USER rules on it; the ruling is
// TERMINAL (a ruled card never re-flags). NEVER an auto-move, NEVER a batch
// fix. This table is the append-only RULING LEDGER that makes a ruling
// terminal — the detector's own read path (AlignmentReviewService.computeFlags)
// excludes any achievementId with a ruling row here.
//
// user_id is `uuid` with a declared FK — mirrors drive_export.ts (the newest
// precedent this lane's own brief names): CLAUDE.md's corrected 2026-06-22 law
// ("identity = per-user users.id uuid, text-keyed user_id tables are migration
// debt") applies to every NEW table, even though achievements.user_id itself
// is still text (FROZEN, untouched — this table only POINTS AT an achievement
// by id, it does not reshape the achievements table itself).
//
// achievement_id is a plain uuid (no FK) — achievements.id IS a uuid (the
// shared `id()` helper), but achievements rows are superseded-not-deleted
// (CorrectionRehomeService's §6 supersede mints a NEW id on every re-home), so
// a hard FK would either cascade-orphan a terminal ruling the moment a card is
// re-homed again, or block the very re-home a 'moved' ruling triggers. The
// ruling ledger is provenance ABOUT a specific card-id-at-ruling-time, not a
// live-consistency constraint — same posture role_events.role_id takes on
// roles.id ("soft FK -> roles.id, indexed, enforced in the service").
//
// Append-only + terminal: NOTHING here is ever updated or deleted (the
// no-deletions law). A user who changes their mind about an already-ruled
// card creates a NEW row (a fresh ruling supersedes the old one for read
// purposes — AlignmentReviewService reads the LATEST ruling per achievementId
// — but the prior ruling stays queryable forever, exactly like achievements'
// own supersede chain).
// =============================================================================

export const achievementAlignmentRulings = pgTable(
  'achievement_alignment_rulings',
  {
    id: id(),
    /** The real users.id uuid this ruling belongs to. Declared FK (both sides
     *  uuid) — mirrors user_drive_grants.user_id / drive_exports.user_id. */
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** The achievements.id this ruling is about. Plain uuid, no FK — see
     *  module header (a hard FK would fight the supersede-mints-a-new-id
     *  shape CorrectionRehomeService already uses). */
    achievementId: uuid('achievement_id').notNull(),
    /** 'moved' | 'kept' | 'flagged_split' | 'merged'. varchar, not a DB enum —
     *  this codebase's house convention (jobs.source, entitlementGrants.label,
     *  achievements.status all take a plain varchar over a Postgres enum so a
     *  future ruling value is a data value, not a migration) — proven again
     *  here: 'merged' (RESUME-POLISH lane, the 'near_duplicate' signal's
     *  MERGE action) is a pure additive data value, zero migration. */
    ruling: varchar('ruling', { length: 16 }).notNull(),
    /** Set only when ruling==='moved' — the role the card was re-homed to.
     *  Plain uuid, no FK (roles rows follow the same soft-FK convention
     *  role_events.role_id already takes on roles.id in this package). */
    movedToRoleId: uuid('moved_to_role_id'),
    /** ADDITIVE, set only when ruling==='merged' — the SURVIVOR achievement
     *  this card was merged INTO (achievements.merged_into carries the same
     *  value on the achievement row itself; this column is the ruling
     *  ledger's own record of the same fact, so the ledger stays a complete,
     *  self-contained audit trail without a join back to achievements for
     *  every past ruling). Plain uuid, no FK — same soft-FK convention as
     *  movedToRoleId above and achievements.merged_into itself. */
    mergedIntoAchievementId: uuid('merged_into_achievement_id'),
    /** The flag's own evidence, frozen at ruling time — the exact signals
     *  (kind + matched text + suggestedRoleId) the user was shown when they
     *  ruled, so a later change to the detector's heuristics can never
     *  retroactively rewrite what evidence a past ruling was actually made
     *  against. Written verbatim from AlignmentReviewService's AlignmentFlag,
     *  never regenerated. */
    evidenceSnapshot: jsonb('evidence_snapshot'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('achievement_alignment_rulings_user_idx').on(t.userId),
    achievementIdx: index('achievement_alignment_rulings_achievement_idx').on(t.achievementId),
  }),
);

/** The four terminal ruling values a user can pick for a flagged suspect.
 *  'merged' is additive (RESUME-POLISH lane) — the 'near_duplicate' cluster
 *  signal's MERGE action. */
export type AlignmentRulingValue = 'moved' | 'kept' | 'flagged_split' | 'merged';
